import { httpDelete, httpGet, httpPatch, httpPost, httpPut } from "../http";
import { debtEndpoints } from "../endpoints";
import { femaCapture } from "../../fema/capture";
import type {
  ApiResponse,
  CustomerDebtRecord,
  DebtResponse,
  DebtUpsertRequest,
  FreePostDataResponse,
  FreePostSharingPayload,
  PaymentOfferRecord,
  PaymentOfferResponse,
  PaymentOfferUpsertRequest,
  RequestConfig,
  CustomerSharingResponse,
  CustomerSharingUpsertRequest,
} from "../types";

type AnyResponse<T> = { data?: T } | ApiResponse<T> | T;

const buildSuccessResponse = <T>(data: T, message: string): ApiResponse<T> => ({
  success: true,
  data,
  message,
  timestamp: new Date().toISOString(),
});

const extractPayload = <T>(response: AnyResponse<T> | AnyResponse<T[]>): T | T[] => {
  if (response && typeof response === "object") {
    const withData = response as { data?: T | T[] };
    if (Array.isArray(withData.data)) {
      return withData.data as T[];
    }
    if (withData.data !== undefined) {
      return withData.data as T;
    }
  }
  return response as T;
};

const extractArrayPayload = <T>(response: AnyResponse<T[]>): T[] => {
  if (response && typeof response === "object") {
    const withData = response as { data?: T | T[] | T[][] };
    if (withData.data !== undefined) {
      if (Array.isArray(withData.data)) {
        // Check if it's a nested array (array of arrays)
        if (withData.data.length > 0 && Array.isArray(withData.data[0])) {
          // Flatten nested arrays
          return (withData.data as unknown as T[][]).flat() as T[];
        }
        // It's a flat array
        return withData.data as T[];
      }
    }
  }
  // If response itself is an array (direct response)
  if (Array.isArray(response)) {
    if (response.length > 0 && Array.isArray(response[0])) {
      return (response as unknown as T[][]).flat() as T[];
    }
    return response as T[];
  }
  return [];
};

const extractSingleOrArrayPayload = <T>(
  response: AnyResponse<T | T[]>
): T | T[] => {
  if (response && typeof response === "object") {
    const withData = response as { data?: T | T[] | (T | T[])[] };
    if (withData.data !== undefined) {
      if (Array.isArray(withData.data)) {
        // Check if it's a nested array
        if (withData.data.length > 0 && Array.isArray(withData.data[0])) {
          // Flatten nested arrays
          return (withData.data as unknown as (T | T[])[]).flat() as T[];
        }
        // It's a flat array
        return withData.data as T[];
      }
      // It's a single item
      return withData.data as T;
    }
  }
  // If response itself is an array (direct response)
  if (Array.isArray(response)) {
    if (response.length > 0 && Array.isArray(response[0])) {
      return (response as unknown as (T | T[])[]).flat() as T[];
    }
    return response as T[];
  }
  // Single item or fallback
  return response as T;
};

export class DebtsService {
  static async getDebts(
    customerId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerDebtRecord[]>> {
    try {
      const response = await httpGet<AnyResponse<CustomerDebtRecord[]>>(
        debtEndpoints.list(customerId),
        config
      );

      const data = extractArrayPayload<CustomerDebtRecord>(response.data);

      return buildSuccessResponse(data, "Debts fetched successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("debts_fetch_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async getPriorityDebts(
    customerId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerDebtRecord[]>> {
    try {
      const response = await httpGet<AnyResponse<CustomerDebtRecord[]>>(
        debtEndpoints.priorityList(customerId),
        config
      );

      const data = extractArrayPayload<CustomerDebtRecord>(response.data);

      return buildSuccessResponse(
        data,
        "Priority debts fetched successfully"
      );
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("priority_debts_fetch_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async getNonPriorityDebts(
    customerId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerDebtRecord[]>> {
    try {
      const response = await httpGet<AnyResponse<CustomerDebtRecord[]>>(
        debtEndpoints.nonPriorityList(customerId),
        config
      );

      const data = extractArrayPayload<CustomerDebtRecord>(response.data);

      return buildSuccessResponse(
        data,
        "Non-priority debts fetched successfully"
      );
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("non_priority_debts_fetch_error", error, {
          category: "api",
          severity: "error",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async createDebt(
    payload: DebtUpsertRequest,
    config: RequestConfig = {}
  ): Promise<DebtResponse> {
    try {
      const response = await httpPost<AnyResponse<CustomerDebtRecord>>(
        debtEndpoints.create(),
        payload,
        config
      );

      const data = extractPayload<CustomerDebtRecord>(response.data) as CustomerDebtRecord;
      return buildSuccessResponse(data, "Debt created successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("debts_create_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async updateDebt(
    payload: DebtUpsertRequest,
    config: RequestConfig = {}
  ): Promise<DebtResponse> {
    try {
      // Use PUT for updates (id must be present in payload as per API contract)
      const response = await httpPut<AnyResponse<CustomerDebtRecord>>(
        debtEndpoints.update(),
        payload,
        config
      );

      const data = extractPayload<CustomerDebtRecord>(response.data) as CustomerDebtRecord;
      return buildSuccessResponse(data, "Debt updated successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("debts_update_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId, debtId: payload.id },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async deleteDebt(
    customerId: string | number,
    debtId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<unknown>> {
    try {
      const response = await httpDelete<AnyResponse<unknown>>(
        debtEndpoints.delete(customerId, debtId),
        config
      );

      const data = extractPayload<unknown>(response.data);
      return buildSuccessResponse(data, "Debt deleted successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("debts_delete_error", error, {
          category: "api",
          severity: "error",
          context: { customerId, debtId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async getPaymentOffer(
    customerId: string | number,
    lenderName: string,
    accountNumber: string,
    config: RequestConfig = {}
  ): Promise<PaymentOfferResponse> {
    try {
      const response = await httpGet<AnyResponse<PaymentOfferRecord | PaymentOfferRecord[]>>(
        debtEndpoints.paymentOffer.get(customerId, lenderName, accountNumber),
        config
      );

      const data = extractSingleOrArrayPayload<PaymentOfferRecord>(response.data);
      return buildSuccessResponse(data, "Payment offer fetched successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("payment_offer_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId, lenderName, accountNumber },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async createPaymentOffer(
    payload: PaymentOfferUpsertRequest,
    config: RequestConfig = {}
  ): Promise<PaymentOfferResponse> {
    try {
      const response = await httpPost<AnyResponse<PaymentOfferRecord>>(
        debtEndpoints.paymentOffer.create(),
        payload,
        config
      );

      const data = extractPayload<PaymentOfferRecord>(response.data) as PaymentOfferRecord;
      return buildSuccessResponse(data, "Payment offer created successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("payment_offer_create_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId, debtId: payload.debtId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async updatePaymentOffer(
    paymentOfferId: string | number,
    payload: PaymentOfferUpsertRequest,
    config: RequestConfig = {}
  ): Promise<PaymentOfferResponse> {
    try {
      const response = await httpPut<AnyResponse<PaymentOfferRecord>>(
        debtEndpoints.paymentOffer.update(paymentOfferId),
        payload,
        config
      );

      const data = extractPayload<PaymentOfferRecord>(response.data) as PaymentOfferRecord;
      return buildSuccessResponse(data, "Payment offer updated successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("payment_offer_update_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId, debtId: payload.debtId, paymentOfferId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async deletePaymentOffer(
    paymentOfferId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<unknown>> {
    try {
      const response = await httpDelete<AnyResponse<unknown>>(
        debtEndpoints.paymentOffer.delete(paymentOfferId),
        config
      );

      const data = extractPayload<unknown>(response.data);
      return buildSuccessResponse(data, "Payment offer deleted successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("payment_offer_delete_error", error, {
          category: "api",
          severity: "warning",
          context: { paymentOfferId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async getFreePostSharing(
    customerId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<FreePostDataResponse[]>> {
    try {
      const response = await httpGet<AnyResponse<FreePostDataResponse[]>>(
        debtEndpoints.freePostSharing.list(customerId),
        config
      );

      const data = extractArrayPayload<FreePostDataResponse>(response.data);
      return buildSuccessResponse(data, "Freepost sharing fetched successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("freepost_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async createFreePostSharing(
    payload: FreePostSharingPayload,
    config: RequestConfig = {}
  ): Promise<ApiResponse<FreePostDataResponse>> {
    try {
      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete (sanitizedPayload as any).editable;

      const response = await httpPost<AnyResponse<FreePostDataResponse>>(
        debtEndpoints.freePostSharing.create(),
        sanitizedPayload,
        config
      );

      const data = extractPayload<FreePostDataResponse>(response.data) as FreePostDataResponse;
      return buildSuccessResponse(data, "Freepost sharing created successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("freepost_create_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId, debtId: payload.debtId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async updateFreePostSharing(
    payload: FreePostSharingPayload,
    config: RequestConfig = {}
  ): Promise<ApiResponse<FreePostDataResponse>> {
    try {
      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete (sanitizedPayload as any).editable;

      const endpoint =
        typeof payload.id === "number" || typeof payload.id === "string"
          ? debtEndpoints.freePostSharing.updateById(payload.id)
          : debtEndpoints.freePostSharing.update();

      const response = await httpPatch<AnyResponse<FreePostDataResponse>>(
        endpoint,
        sanitizedPayload,
        config
      );

      const data = extractPayload<FreePostDataResponse>(
        response.data
      ) as FreePostDataResponse;
      return buildSuccessResponse(data, "Freepost sharing updated successfully");
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("freepost_update_error", error, {
          category: "api",
          severity: "error",
          context: {
            customerId: payload.customerId,
            debtId: payload.debtId,
            id: payload.id,
          },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async getCustomerSharing(
    customerId: string | number,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerSharingResponse[]>> {
    try {
      const response = await httpGet<AnyResponse<CustomerSharingResponse[]>>(
        debtEndpoints.customerSharing.list(customerId),
        config
      );

      const data = extractArrayPayload<CustomerSharingResponse>(response.data);
      return buildSuccessResponse(
        data,
        "Customer sharing fetched successfully"
      );
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("customer_sharing_fetch_error", error, {
          category: "api",
          severity: "warning",
          context: { customerId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async createCustomerSharing(
    payload: CustomerSharingUpsertRequest,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerSharingResponse>> {
    try {
      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete (sanitizedPayload as any).editable;

      const response = await httpPost<AnyResponse<CustomerSharingResponse>>(
        debtEndpoints.customerSharing.create(),
        sanitizedPayload,
        config
      );

      const data = extractPayload<CustomerSharingResponse>(
        response.data
      ) as CustomerSharingResponse;
      return buildSuccessResponse(
        data,
        "Customer sharing created successfully"
      );
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("customer_sharing_create_error", error, {
          category: "api",
          severity: "error",
          context: { customerId: payload.customerId, debtId: payload.debtId },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }

  static async updateCustomerSharing(
    payload: CustomerSharingUpsertRequest,
    config: RequestConfig = {}
  ): Promise<ApiResponse<CustomerSharingResponse>> {
    try {
      // Ensure "editable" is not sent to the API as the backend model does not support it
      const sanitizedPayload = { ...payload };
      delete (sanitizedPayload as any).editable;

      const endpoint =
        typeof payload.id === "number" || typeof payload.id === "string"
          ? debtEndpoints.customerSharing.updateById(payload.id)
          : debtEndpoints.customerSharing.update();

      const response = await httpPatch<AnyResponse<CustomerSharingResponse>>(
        endpoint,
        sanitizedPayload,
        config
      );

      const data = extractPayload<CustomerSharingResponse>(
        response.data
      ) as CustomerSharingResponse;
      return buildSuccessResponse(
        data,
        "Customer sharing updated successfully"
      );
    } catch (error) {
      if (!config.skipErrorCapture) {
        femaCapture("customer_sharing_update_error", error, {
          category: "api",
          severity: "error",
          context: {
            customerId: payload.customerId,
            debtId: payload.debtId,
            id: payload.id,
          },
          skipSanitization: false,
          user: {},
        });
      }
      throw error;
    }
  }
}

export default DebtsService;

